import { CircularProgress, Dialog } from '@mui/material';
import {
    FunctionComponent,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';

import { ReactComponent as AttachmentIcon } from 'src/assets/icons/attachment.svg';
import { ReactComponent as CloseIcon } from 'src/assets/icons/close.svg';
import useFileUploadWithValidation, {
    UploadedFile
} from 'src/hooks/useFileUploadWithValidation';
import { generateUniqueId } from 'src/utils/attachments';
import color from 'src/theme/color';
import AttachmentItem from 'src/shared/AttachmentItem';
import UploadProgress from 'src/shared/UploadProgress';
import {
    AttachmentIconButton,
    AttachmentsContainer,
    ButtonContainer,
    CloseButton,
    ModalContent,
    ModalTextField,
    dialogPaperProps,
    DialogTitle,
    DialogDescription,
    ErrorMessage,
    HiddenFileInput,
    FooterButton,
    CancelButton
} from './styled';

interface FeedbackBugModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    onSubmit: (description: string, attachmentIds: number[]) => Promise<void>;
}

const FeedbackBugModal: FunctionComponent<FeedbackBugModalProps> = ({
    open,
    onClose,
    title,
    description: modalDescription,
    onSubmit
}) => {
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesSelect = useFileUploadWithValidation({
        onFilesUpdate: setFiles,
        onError: setFileError
    });

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const selectedFilesWithIds = Array.from(e.target.files).map(
                    (file) => ({
                        file,
                        id: generateUniqueId()
                    })
                );
                handleFilesSelect(selectedFilesWithIds);
            }
            // Reset the input so the same file can be selected again
            if (e.target) {
                e.target.value = '';
            }
        },
        [handleFilesSelect]
    );

    const handleAttachmentClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const removeAttachment = useCallback((idToRemove: number): void => {
        setFiles((prevFiles) =>
            prevFiles.filter((file) => {
                // Match by fileId if it exists, otherwise match by local id
                if (file.fileId) {
                    return file.fileId !== idToRemove;
                }
                return file.id !== idToRemove;
            })
        );
    }, []);

    const cancelUpload = useCallback(
        (fileId: number): void => {
            const fileToCancel = files.find((f) => f.id === fileId);

            if (fileToCancel && fileToCancel.uploadController) {
                fileToCancel.uploadController.abort();
            }

            removeAttachment(fileId);
        },
        [files, removeAttachment]
    );

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);
        try {
            const attachmentIds = files
                .filter((f) => f.fileId && f.status === 'completed')
                .map((f) => Number(f.fileId));
            await onSubmit(description, attachmentIds);
        } finally {
            setIsSubmitting(false);
        }
    }, [files, description, onSubmit]);

    // Reset files when dialog closes
    useEffect(() => {
        if (!open) {
            setDescription('');
            setFiles([]);
            setFileError(null);
            setIsSubmitting(false);
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={dialogPaperProps}
            maxWidth="md"
            fullWidth
        >
            <CloseButton onClick={onClose} disabled={isSubmitting}>
                <CloseIcon />
            </CloseButton>
            <DialogTitle>{title}</DialogTitle>
            {modalDescription && (
                <DialogDescription>{modalDescription}</DialogDescription>
            )}
            <ModalContent>
                <ModalTextField
                    multiline
                    rows={6}
                    variant="outlined"
                    fullWidth
                    placeholder="Description..."
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setDescription(e.target.value)
                    }
                />
                <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                    multiple
                    onChange={handleFileChange}
                />
                <AttachmentsContainer>
                    <AttachmentIconButton onClick={handleAttachmentClick}>
                        <AttachmentIcon color={color.lightningYellow} />
                    </AttachmentIconButton>
                    {files
                        .filter((file) => file.status === 'completed')
                        .map((attachedFile) => (
                            <AttachmentItem
                                key={`attachment-${attachedFile.id}`}
                                file={{
                                    id:
                                        Number(attachedFile.fileId) ||
                                        generateUniqueId(),
                                    name: attachedFile.file.name,
                                    type: attachedFile.file.type
                                }}
                                index={Number(attachedFile.fileId)}
                                removeAttachment={removeAttachment}
                            />
                        ))}
                </AttachmentsContainer>
                <UploadProgress files={files} onCancelUpload={cancelUpload} />
                {fileError && <ErrorMessage>{fileError}</ErrorMessage>}
                <ButtonContainer>
                    <CancelButton onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </CancelButton>
                    <FooterButton
                        onClick={handleSubmit}
                        disabled={
                            !description.trim() ||
                            files.some((file) => file.status === 'uploading') ||
                            isSubmitting
                        }
                    >
                        {isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Submit'
                        )}
                    </FooterButton>
                </ButtonContainer>
            </ModalContent>
        </Dialog>
    );
};

export default FeedbackBugModal;
